import { BaseGridRenderer, RenderConfig } from './BaseGridRenderer';
import { SquareGrid } from './SquareGrid';
import { Cell, CellType, CellState } from './Cell';

export class SquareGridRenderer extends BaseGridRenderer {
  protected grid: SquareGrid;

  constructor(grid: SquareGrid, config: RenderConfig) {
    super(grid, config);
    this.grid = grid;
  }

  resizeCanvas(): void {
    const width = this.grid.cols * this.cellSize;
    const height = this.grid.rows * this.cellSize;
    this.canvas.width = width * this.dpr;
    this.canvas.height = height * this.dpr;
    this.canvas.style.width = `${width}px`;
    this.canvas.style.height = `${height}px`;
    this.ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
  }

  render(hoverCell?: Cell | null, previewType?: 'wall' | 'erase' | 'start' | 'goal' | null): void {
    this.clear();
    this.renderCells();
    if (this.showGrid) {
      this.renderGridLines();
    }
    if (hoverCell && previewType) {
      this.renderCellPreview(hoverCell, previewType);
      this.renderHoverHighlight(hoverCell);
    }
  }

  cellFromPoint(x: number, y: number): { row: number; col: number } | null {
    const col = Math.floor(x / this.cellSize);
    const row = Math.floor(y / this.cellSize);

    if (this.grid.isInBounds(row, col)) {
      return { row, col };
    }

    return null;
  }

  private renderCells(): void {
    for (let row = 0; row < this.grid.rows; row++) {
      for (let col = 0; col < this.grid.cols; col++) {
        const cell = this.grid.getCellAt(row, col);
        this.renderCell(cell);
      }
    }
  }

  private renderCell(cell: Cell): void {
    const x = cell.col * this.cellSize;
    const y = cell.row * this.cellSize;

    const color = this.getCellColor(cell);
    this.ctx.fillStyle = color;
    this.ctx.fillRect(x, y, this.cellSize, this.cellSize);

    if (cell.type === CellType.START || cell.type === CellType.GOAL) {
      this.renderMarker(cell);
    }
  }

  private getCellColor(cell: Cell): string {
    switch (cell.type) {
      case CellType.WALL:
        return this.colors.wall;
      case CellType.START:
        return this.colors.start;
      case CellType.GOAL:
        return this.colors.goal;
      default:
        switch (cell.state) {
          case CellState.OPEN:
            return this.colors.open;
          case CellState.CLOSED:
            return this.colors.closed;
          case CellState.PATH:
            return this.colors.path;
          default:
            return this.colors.empty;
        }
    }
  }

  private renderMarker(cell: Cell): void {
    const x = cell.col * this.cellSize + this.cellSize / 2;
    const y = cell.row * this.cellSize + this.cellSize / 2;
    const radius = this.cellSize / 3;

    this.ctx.fillStyle = '#ffffff';
    this.ctx.beginPath();
    this.ctx.arc(x, y, radius, 0, Math.PI * 2);
    this.ctx.fill();
  }

  private renderGridLines(): void {
    this.ctx.strokeStyle = this.colors.grid;
    this.ctx.lineWidth = 1;

    for (let row = 0; row <= this.grid.rows; row++) {
      this.ctx.beginPath();
      this.ctx.moveTo(0, row * this.cellSize);
      this.ctx.lineTo(this.grid.cols * this.cellSize, row * this.cellSize);
      this.ctx.stroke();
    }

    for (let col = 0; col <= this.grid.cols; col++) {
      this.ctx.beginPath();
      this.ctx.moveTo(col * this.cellSize, 0);
      this.ctx.lineTo(col * this.cellSize, this.grid.rows * this.cellSize);
      this.ctx.stroke();
    }
  }

  private renderCellPreview(cell: Cell, previewType: 'wall' | 'erase' | 'start' | 'goal'): void {
    const x = cell.col * this.cellSize;
    const y = cell.row * this.cellSize;

    this.ctx.globalAlpha = 0.5;

    switch (previewType) {
      case 'wall':
        if (cell.type !== CellType.START && cell.type !== CellType.GOAL) {
          this.ctx.fillStyle = this.colors.wall;
          this.ctx.fillRect(x, y, this.cellSize, this.cellSize);
        }
        break;

      case 'erase':
        if (cell.type === CellType.WALL) {
          this.ctx.fillStyle = this.colors.empty;
          this.ctx.fillRect(x, y, this.cellSize, this.cellSize);
        }
        break;

      case 'start':
        this.ctx.fillStyle = this.colors.start;
        this.ctx.fillRect(x, y, this.cellSize, this.cellSize);
        this.ctx.globalAlpha = 1.0;
        this.ctx.fillStyle = '#ffffff';
        this.ctx.beginPath();
        this.ctx.arc(x + this.cellSize / 2, y + this.cellSize / 2, this.cellSize / 3, 0, Math.PI * 2);
        this.ctx.fill();
        this.ctx.globalAlpha = 0.5;
        break;

      case 'goal':
        this.ctx.fillStyle = this.colors.goal;
        this.ctx.fillRect(x, y, this.cellSize, this.cellSize);
        this.ctx.globalAlpha = 1.0;
        this.ctx.fillStyle = '#ffffff';
        this.ctx.beginPath();
        this.ctx.arc(x + this.cellSize / 2, y + this.cellSize / 2, this.cellSize / 3, 0, Math.PI * 2);
        this.ctx.fill();
        this.ctx.globalAlpha = 0.5;
        break;
    }

    this.ctx.globalAlpha = 1.0;
  }

  private renderHoverHighlight(cell: Cell): void {
    const x = cell.col * this.cellSize;
    const y = cell.row * this.cellSize;

    this.ctx.strokeStyle = '#ffd700';
    this.ctx.lineWidth = 2;
    this.ctx.strokeRect(x + 1, y + 1, this.cellSize - 2, this.cellSize - 2);
  }
}
